import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { Moment } from 'moment';

import type { FetchEventsResponse } from '@appTypes/esb';
import type { InvitedUser } from '@appTypes/esb/advancements/events/event';
import { SBL_5060_ENABLE_HTML_EVENT_DESCRIPTION } from '@config/config';
import { recurrenceTypes, recurrenceTypesIds } from '@modules/events/constants';
import { parseBBCode } from '@modules/utils';
import momentReactCalendar from '@modules/utils/momentReactCalendar';
import momentWithOffset from '@modules/utils/momentWithOffset';

export function getMaxEndDate(
  params:
    | {
        startDate: Date;
        isRecurrent: true;
        recurrenceTypeId: number;
      }
    | {
        isRecurrent: false;
      },
): Date {
  const NO_MAX_DATE = DateTime.now().plus({ years: 10 }).toJSDate();

  if (!params.isRecurrent || !params.startDate) return NO_MAX_DATE;

  const baseDate = DateTime.fromJSDate(params.startDate);
  const recurrenceType = recurrenceTypes.find(
    item => item.id === params.recurrenceTypeId,
  );
  if (!recurrenceType) return NO_MAX_DATE;

  switch (params.recurrenceTypeId) {
    case recurrenceTypesIds.DAILY:
    case recurrenceTypesIds.DAILY_WEEKDAYS: {
      const maxDate = baseDate
        .plus({
          days: recurrenceType.maxRangeCount - 1,
        })
        .toJSDate(); // -1 to count for current day
      return maxDate;
    }
    case recurrenceTypesIds.WEEKLY_ONE_DAY:
    case recurrenceTypesIds.WEEKLY_MULTIPLE_DAYS: {
      const maxDate = baseDate
        .plus({
          weeks: recurrenceType.maxRangeCount,
        })
        .toJSDate();
      return maxDate;
    }
    case recurrenceTypesIds.MONTHLY:
    case recurrenceTypesIds.MONTHLY_NTH_DAY:
    case recurrenceTypesIds.MONTHLY_MULTIPLE_DAYS: {
      const maxDate = baseDate
        .plus({ months: recurrenceType.maxRangeCount })
        .toJSDate();
      return maxDate;
    }
    case recurrenceTypesIds.YEARLY:
    case recurrenceTypesIds.YEARLY_NTH_DAY: {
      const maxDate = baseDate
        .plus({ years: recurrenceType.maxRangeCount })
        .toJSDate();
      return maxDate;
    }
    default: {
      return NO_MAX_DATE;
    }
  }
}

// Copy & paste from calendar epic
export function transformFetchEvents(
  events: FetchEventsResponse,
  params: {
    timezoneCode: string;
    loggedInUserId: number;
  },
) {
  if (!events) return [];

  const { timezoneCode, loggedInUserId } = params;

  const transformed = events.map(event => {
    const startDateTime = momentWithOffset(event.startDate, timezoneCode);
    const updatedStart = momentReactCalendar(startDateTime, event.startDate);
    const endDateTime = momentWithOffset(event.endDate, timezoneCode);
    const updatedEnd = momentReactCalendar(endDateTime, event.endDate);
    const startTime = startDateTime.format('HH:mm');
    const endTime = endDateTime.format('HH:mm');
    const isAllDay = startTime === '00:00' && endTime === '23:59';
    const invitedUser: InvitedUser =
      (event.invitedUsers &&
        event.invitedUsers.find(user => loggedInUserId === user.userId)) ||
      ({} as InvitedUser);
    return {
      title: event.name,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      start: (updatedStart as any)._d as Moment,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      end: (updatedEnd as any)._d as Moment,
      allDay: isAllDay,
      id: event.id,
      resource: {
        ...event,
        isEvent: !event.activityTypeId,
        description: SBL_5060_ENABLE_HTML_EVENT_DESCRIPTION
          ? DOMPurify.sanitize(event.description || '')
          : parseBBCode(event.description || ''),
        canRsvp: !!event.rsvp,
        userRsvp: invitedUser.rsvpCode,
      },
    };
  });

  return transformed;
}
